import {
  AccountTransactionChildWithDerivedStatuses,
  isUndecodedContractInteraction,
} from '@mls-wallet/shared-types';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { getContractDisplayName, getOpaqueContractDisplayName } from './utils';
import { TokenLogo } from '@/components/shared/token-logo';

interface TransactionDetailTitleProps {
  transaction: AccountTransactionChildWithDerivedStatuses;
  variant?: 'cell' | 'detail';
  className?: string;
}

export function TransactionDetailTitle({
  transaction,
  variant = 'cell',
  className = '',
}: TransactionDetailTitleProps) {
  // Mock USD values for display
  // TODO: DEN-27 Delete when token price fetching is implemented
  const getUsdValue = (amount: string, assetSymbol: string) => {
    // This would come from your API in a real app
    const mockRates: Record<string, number> = {
      ETH: 3000,
      USDC: 1,
      USDT: 1,
    };
    const rate = mockRates[assetSymbol] || 0;
    const usdValue = parseFloat(amount) * rate;
    return `$${usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  getUsdValue('0', 'ETH');

  const detailClasses = variant === 'detail' ? 'text-base' : 'text-md';
  const iconSize = variant === 'detail' ? 'h-5 w-5' : 'h-4 w-4';
  const isOpaqueContractCall = isUndecodedContractInteraction(transaction);

  // Check if transaction has been executed
  const isExecuted = transaction.executionStatus === 'completed';

  // Get appropriate prefix for token transfers
  const getTokenTransferPrefix = () => {
    return isExecuted ? 'Sent' : 'Send';
  };

  // Get appropriate prefix for contract calls
  const getContractCallPrefix = () => {
    return 'Contract call';
  };

  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <div className="mt-0.5 ">
        <ArrowUpRight className={`${iconSize} text-rose-400`} />
      </div>
      <div className="flex flex-col gap-1">
        {transaction.type === 'TOKEN_TRANSFER' ? (
          <div>
            <span
              className={`font-semibold whitespace-nowrap text-xl ${detailClasses} inline-flex items-center gap-1`}
            >
              {getTokenTransferPrefix()} {transaction.displayAmount}{' '}
              <TokenLogo
                symbol={transaction.asset.symbol}
                logoUrl={transaction.asset.logoUrl}
                size="md"
              />
              {transaction.asset.symbol}
            </span>
            <span
              className={`font-normal text-muted-foreground whitespace-nowrap text-xl ${detailClasses}`}
            >
              {/*
              &nbsp;·&nbsp;
              {getUsdValue(transaction.displayAmount, transaction.asset.symbol)}
              */}
            </span>
          </div>
        ) : transaction.type === 'CONTRACT_INTERACTION' ? (
          <div className="flex items-start flex-col">
            <span
              className={`font-semibold whitespace-nowrap text-xl ${detailClasses}`}
            >
              {getContractCallPrefix()}
            </span>
            {/* &nbsp;·&nbsp; */}
            <span
              className={`text-muted-foreground flex items-center whitespace-nowrap ${detailClasses}`}
            >
              <span className="font-normal text-xl">
                {isOpaqueContractCall
                  ? getOpaqueContractDisplayName(transaction)
                  : getContractDisplayName(transaction)}
              </span>
              {/* Case: decoded calls have function metadata to display. */}
              {!isOpaqueContractCall ? (
                <>
                  <ArrowRight className="h-3 w-3 mx-1" />
                  <span className="font-normal text-xl">
                    {transaction.functionName}
                  </span>
                </>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
